import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment-timezone";
import {
  fetchEvents,
  fetchTags,
  fetchExportEvents,
} from "../../../store/slices/scheduleSlice";

const useScheduleData = (currentDate, currentView) => {
  const dispatch = useDispatch();
  const { events, tags, exportEvents } = useSelector((state) => state.schedule);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [eventsError, setEventsError] = useState(null);
  const [tagsError, setTagsError] = useState(null);
  const [exportError, setExportError] = useState(null);

  const clearEventsError = () => setEventsError(null);
  const clearTagsError = () => setTagsError(null);
  const clearExportError = () => setExportError(null);

  const tagsLoaded = useRef(false);
  const exportLoaded = useRef(false);

  const fetchTagsData = useCallback(async () => {
    setTagsLoading(true);
    setTagsError(null);
    try {
      await dispatch(fetchTags()).unwrap();
      tagsLoaded.current = true;
    } catch (err) {
      setTagsError(err);
    } finally {
      setTagsLoading(false);
    }
  }, [dispatch]);

  const fetchEventsData = useCallback(
    async (date, view) => {
      setEventsLoading(true);
      setEventsError(null);
      try {
        let start, end;
        if (view === "agenda") {
          start = moment(date).startOf("day");
          end = moment(date)
            .startOf("day")
            .add(30, "days")
            .subtract(1, "second");
        } else {
          // Add a small buffer to avoid missing boundary-day events
          // (e.g. late Saturday occurrences due to timezone/exclusive-end handling).
          start = moment(date).startOf(view).subtract(1, "day");
          end = moment(date).endOf(view).add(1, "day");
        }
        await dispatch(fetchEvents({ start, end })).unwrap();
      } catch (err) {
        setEventsError(err);
      } finally {
        setEventsLoading(false);
      }
    },
    [dispatch]
  );

  const fetchExportData = useCallback(async () => {
    setExportLoading(true);
    setExportError(null);
    try {
      await dispatch(fetchExportEvents()).unwrap();
      exportLoaded.current = true;
    } catch (err) {
      setExportError(err);
    } finally {
      setExportLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (!tagsLoaded.current) fetchTagsData();
  }, []);

  useEffect(() => {
    fetchEventsData(currentDate, currentView);
  }, [currentDate, currentView]);

  return {
    events,
    tags,
    exportEvents,
    eventsLoading,
    tagsLoading,
    exportLoading,
    eventsError,
    tagsError,
    exportError,
    clearEventsError,
    clearTagsError,
    clearExportError,
    refetchEvents: fetchEventsData,
    refetchTags: fetchTagsData,
    refetchExport: fetchExportData,
    refetchAll: async () => {
      await Promise.all([
        fetchTagsData(),
        fetchEventsData(currentDate, currentView),
        fetchExportData(),
      ]);
    },
  };
};

export default useScheduleData;
